import React from "react";
import { Calendar, Sparkles, ArrowDownLeft, ArrowUpRight } from "lucide-react";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";

import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import CategoryEditPopover from "./CategoryEditPopover";

// FIN-2 — TransactionRow.
//
// Densidade alta, tipografia mono pro valor, hover com glow border cyan
// + lift sutil + reveal de CTA "Falar com Nexo" (T12).
//
// Variant compact (mobile <md): vira card vertical em vez de tr.

const fmtBRL = (v) => Number(Math.abs(v) ?? 0).toLocaleString("pt-BR", {
  style: "currency", currency: "BRL",
});

const fmtDateBR = (iso) => {
  if (!iso) return "—";
  try {
    return format(parseISO(iso), "dd 'de' MMM.", { locale: ptBR });
  } catch {
    return iso;
  }
};

const ACCOUNT_TYPE_LABEL = {
  checking: "Conta", savings: "Poupança", credit_card: "Cartão",
  investment: "Investimento", cash: "Dinheiro", other: "Outro",
};

// CategoryPillButton precisa retornar UM único elemento pra Radix
// PopoverTrigger asChild conseguir clonar props nele. Badge "Nova" fica
// como sibling fora do trigger.
const CategoryPillButton = React.forwardRef(({ category, ...rest }, ref) => (
  <button
    ref={ref}
    type="button"
    className={cn(
      "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-caption font-medium border transition-all duration-fast ease-smooth",
      "border-subtle bg-surface-card-hover text-foreground hover:border-brand-cyan/40 hover:bg-brand-cyan/10",
    )}
    title="Clique para editar categoria"
    data-testid="extrato-row-category-pill"
    {...rest}
  >
    <span className="truncate max-w-[120px]">{category || "outros"}</span>
  </button>
));
CategoryPillButton.displayName = "CategoryPillButton";

function NewCategoryBadge() {
  return (
    <Badge
      variant="warning"
      className="text-micro inline-flex items-center gap-1 px-1.5 py-0.5"
      data-testid="extrato-row-new-cat-badge"
      title="Nova categoria do mês"
      aria-label="Nova categoria do mês"
    >
      <Sparkles className="w-2.5 h-2.5" aria-hidden="true" />
      Nova
    </Badge>
  );
}

function CategoryPill({ tx, isFirst, categoriesUsed, onPatch }) {
  // Edição inline ativa quando categoriesUsed+onPatch fornecidos.
  return (
    <span className="inline-flex items-center gap-1.5">
      {categoriesUsed && onPatch ? (
        <CategoryEditPopover
          tx={tx}
          categoriesUsed={categoriesUsed}
          onPatch={onPatch}
          trigger={<CategoryPillButton category={tx.category} />}
        />
      ) : (
        <CategoryPillButton category={tx.category} />
      )}
      {isFirst && <NewCategoryBadge />}
    </span>
  );
}

export default function TransactionRow({
  tx,
  onTalkAbout,
  categoriesUsed,
  onPatch,
  variant = "table",
}) {
  const isExpense = tx.type === "expense";
  const sign = isExpense ? "−" : "+";
  const valueColor = isExpense ? "text-status-error" : "text-brand-green";
  const Arrow = isExpense ? ArrowUpRight : ArrowDownLeft;
  const accountType = tx.account_type ? ACCOUNT_TYPE_LABEL[tx.account_type] : null;
  const description = tx.description || tx.title || "Sem descrição";
  const subtitle = tx.title && tx.title !== description ? tx.title : null;
  const isFirst = Boolean(tx.is_first_in_category_this_month);

  if (variant === "compact") {
    // Mobile <md — card vertical
    return (
      <div
        className="group relative glass-card rounded-xl p-4 hover:border-brand-cyan/30 transition-all duration-fast"
        data-testid={`extrato-row-${tx.id}`}
      >
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="min-w-0 flex-1">
            <p className="text-foreground font-semibold truncate uppercase text-caption">
              {description}
            </p>
            {subtitle && (
              <p className="text-foreground-muted text-micro line-clamp-1 mt-0.5">{subtitle}</p>
            )}
          </div>
          <div className={cn("font-mono font-bold text-base shrink-0", valueColor)}>
            {sign}{fmtBRL(tx.amount)}
          </div>
        </div>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <CategoryPill
              tx={tx}
              isFirst={isFirst}
              categoriesUsed={categoriesUsed}
              onPatch={onPatch}
            />
            {tx.account_name && (
              <span className="text-micro text-foreground-muted">
                {tx.account_name}{accountType ? ` · ${accountType}` : ""}
              </span>
            )}
          </div>
          <span className="text-micro text-foreground-muted inline-flex items-center gap-1">
            <Calendar className="w-3 h-3" /> {fmtDateBR(tx.date)}
          </span>
        </div>
        {/* CTA "Falar com Nexo" — sempre visível mobile (sem hover) */}
        {onTalkAbout && (
          <button
            type="button"
            onClick={() => onTalkAbout(tx)}
            className="absolute top-2 right-2 p-1.5 rounded-md bg-brand-cyan/10 text-brand-cyan hover:bg-brand-cyan/20 transition-colors opacity-0 group-hover:opacity-100"
            aria-label="Falar com Nexo sobre esta transação"
            data-testid={`extrato-row-talk-${tx.id}`}
          >
            <Sparkles className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    );
  }

  // Default — table row
  return (
    <tr
      className="group border-b border-subtle/50 transition-all duration-fast hover:bg-surface-card-hover"
      data-testid={`extrato-row-${tx.id}`}
    >
      {/* Descrição */}
      <td className="py-3 pr-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className={cn(
            "w-9 h-9 rounded-lg flex items-center justify-center shrink-0",
            isExpense ? "bg-status-error/10" : "bg-brand-green/10",
          )}>
            <Arrow className={cn("w-4 h-4", isExpense ? "text-status-error" : "text-brand-green")} aria-hidden="true" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-foreground font-semibold uppercase tracking-tight text-caption truncate">
              {description}
            </p>
            {subtitle && (
              <p className="text-foreground-muted text-micro line-clamp-1 mt-0.5">{subtitle}</p>
            )}
          </div>
        </div>
      </td>

      {/* Categoria */}
      <td className="py-3 px-3">
        <CategoryPill
          tx={tx}
          isFirst={isFirst}
          categoriesUsed={categoriesUsed}
          onPatch={onPatch}
        />
      </td>

      {/* Conta */}
      <td className="py-3 px-3">
        {tx.account_name ? (
          <div>
            <p className="text-foreground text-caption font-medium truncate">{tx.account_name}</p>
            <p className="text-foreground-muted text-micro">
              {accountType || "Conta"}{tx.payment_method && tx.payment_method !== "Other" ? ` · ${tx.payment_method}` : ""}
            </p>
          </div>
        ) : (
          <span className="text-foreground-muted text-caption">—</span>
        )}
      </td>

      {/* Data */}
      <td className="py-3 px-3 whitespace-nowrap">
        <span className="text-foreground-muted text-caption inline-flex items-center gap-1">
          <Calendar className="w-3 h-3" aria-hidden="true" /> {fmtDateBR(tx.date)}
        </span>
      </td>

      {/* Valor + CTA hover */}
      <td className="py-3 pl-3 text-right relative whitespace-nowrap">
        <div className={cn("font-mono font-bold text-base tabular-nums", valueColor)}>
          {sign}{fmtBRL(tx.amount)}
        </div>
        {onTalkAbout && (
          <button
            type="button"
            onClick={() => onTalkAbout(tx)}
            className="absolute top-1/2 -translate-y-1/2 right-1 p-1.5 rounded-md bg-brand-cyan/10 text-brand-cyan hover:bg-brand-cyan/25 transition-all opacity-0 group-hover:opacity-100"
            title="Falar com Nexo sobre esta transação"
            aria-label="Falar com Nexo sobre esta transação"
            data-testid={`extrato-row-talk-${tx.id}`}
          >
            <Sparkles className="w-3.5 h-3.5" aria-hidden="true" />
          </button>
        )}
      </td>
    </tr>
  );
}
